<script setup lang="ts">
import type { Category, DealCard, DealFilters } from '#shared/types/deal'
import type { PaginatedResponse } from '#shared/types/api'

// ISR configurado via routeRules no nuxt.config.ts ('/' → isr: 120)
definePageMeta({ layout: 'default' })

const config = useRuntimeConfig()
const route = useRoute()
const { trackEvent } = useUmami()
const pageUrl = computed(() => `${config.public.appUrl}${route.path}`)
const defaultShareImage = `${config.public.appUrl}/og-default.png`
const homeTitle = 'Descontito — Ofertas, Promoções e Cupons em Um Só Lugar'
const homeDescription = 'Acompanhe ofertas em alta, promoções recentes e cupons das maiores lojas do Brasil no Descontito.'

useSeoMeta({
  title: homeTitle,
  description: homeDescription,
  robots: 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1',
  ogTitle: homeTitle,
  ogDescription: homeDescription,
  ogType: 'website',
  ogUrl: pageUrl,
  ogImage: defaultShareImage,
  ogImageAlt: 'Descontito com ofertas e promocoes em destaque',
  twitterTitle: homeTitle,
  twitterDescription: homeDescription,
  twitterImage: defaultShareImage,
  twitterImageAlt: 'Descontito com ofertas e promocoes em destaque',
  twitterCard: 'summary_large_image',
})

useHead({
  link: [
    {
      rel: 'canonical',
      href: pageUrl.value,
    },
  ],
  meta: [
    { name: 'msvalidate.01', content: '0F19C870CA8FDE31B06C6883C92F301D' },
    { property: 'og:image:secure_url', content: defaultShareImage },
    { property: 'og:image:type', content: 'image/png' },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
  ],
})

// Tabs de ordenação
const tabs = [
  { key: 'hot', label: '🔥 Em Alta', sort: 'hot' as const },
  { key: 'new', label: '🕐 Recentes', sort: 'new' as const },
  { key: 'commented', label: '💬 Mais Comentadas', sort: 'commented' as const },
]

const activeTab = ref('hot')
const activeOnly = ref(true)
const currentSort = computed(() => tabs.find(t => t.key === activeTab.value)?.sort ?? 'hot')

const { data: categories } = await useFetch<Category[]>('/categories', {
  baseURL: config.public.apiBaseUrl,
  key: 'home-filter-categories',
  default: () => [],
})

const categoryOptions = computed(() => [
  { label: '🔥 Todas as promoções', value: '/' },
  ...categories.value.map(category => ({
    label: `${category.icon ?? '🏷️'} ${category.name}`,
    value: `/categoria/${category.slug}`,
  })),
])

const categoryFilter = ref('/')

watch(categoryFilter, async (value) => {
  if (!value) return
  trackEvent('home-category-select', { target: value })
  await navigateTo(value)
})

const currentFilters = computed<DealFilters>(() => ({
  sort: currentSort.value,
  status: activeOnly.value ? 'ACTIVE' : undefined,
  limit: 20,
}))

const { data: initialDeals } = await useFetch<PaginatedResponse<DealCard>>('/deals', {
  baseURL: config.public.apiBaseUrl,
  query: currentFilters.value,
  key: `home-deals-initial-${currentSort.value}-${activeOnly.value ? 'active' : 'all'}`,
})

const { deals, loadMore, hasMore, loading, reset } = useInfiniteDeals(currentFilters.value, initialDeals.value)
const schemaDeals = computed(() => deals.value.slice(0, 10))

useSchemaOrg([
  defineBreadcrumb({
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Inicio',
        item: config.public.appUrl,
      },
    ],
  }),
  {
    '@type': 'CollectionPage',
    name: homeTitle,
    description: homeDescription,
    url: pageUrl,
    inLanguage: 'pt-BR',
    isPartOf: {
      '@id': `${config.public.appUrl}/#website`,
    },
  },
  {
    '@type': 'ItemList',
    name: 'Ofertas em destaque no Descontito',
    itemListOrder: 'https://schema.org/ItemListOrderDescending',
    numberOfItems: computed(() => schemaDeals.value.length),
    itemListElement: computed(() =>
      schemaDeals.value.map((deal, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `${config.public.appUrl}/oferta/${deal.slug}`,
        name: deal.title,
      })),
    ),
  },
])

// Reload quando muda a tab ou o filtro de status
watch(activeTab, (value) => {
  trackEvent('deal-sort-change', { page: 'home', sort: value })
  reset(currentFilters.value)
})

watch(activeOnly, (value) => {
  trackEvent('active-filter-toggle', { page: 'home', activeOnly: value })
  reset(currentFilters.value)
})

// Infinite scroll via IntersectionObserver
const sentinel = ref<HTMLElement | null>(null)

onMounted(() => {
  if (!sentinel.value) return
  const observer = new IntersectionObserver(
    ([entry]) => { if (entry?.isIntersecting) loadMore() },
    { rootMargin: '200px' },
  )
  observer.observe(sentinel.value)
  onUnmounted(() => observer.disconnect())
})
</script>

<template>
  <div>
    <div class="mb-5 space-y-3">
      <div class="sm:max-w-xs lg:hidden">
        <USelect
          v-model="categoryFilter"
          :items="categoryOptions"
          label-key="label"
          value-key="value"
          placeholder="Selecione uma categoria"
          size="md"
          class="w-full"
        />
      </div>

      <div class="overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div class="flex min-w-max items-center gap-2 pr-4">
          <UButton
            v-for="tab in tabs"
            :key="tab.key"
            :color="activeTab === tab.key ? 'primary' : 'neutral'"
            :variant="activeTab === tab.key ? 'solid' : 'ghost'"
            size="sm"
            class="shrink-0"
            @click="activeTab = tab.key"
          >
            {{ tab.label }}
          </UButton>

          <UButton
            :color="activeOnly ? 'primary' : 'neutral'"
            :variant="activeOnly ? 'soft' : 'ghost'"
            size="sm"
            :icon="activeOnly ? 'i-heroicons-check-circle' : 'i-heroicons-clock'"
            class="shrink-0"
            @click="activeOnly = !activeOnly"
          >
            {{ activeOnly ? 'Apenas ativas' : 'Todas' }}
          </UButton>
        </div>
      </div>
    </div>

    <!-- Grid de ofertas -->
    <DealGrid :deals="deals" :loading="loading && deals.length === 0" />

    <!-- Carregando mais -->
    <div v-if="loading && deals.length > 0" class="flex justify-center py-8">
      <UIcon name="i-heroicons-arrow-path" class="w-6 h-6 animate-spin text-primary-500" />
    </div>

    <!-- Sentinel para infinite scroll -->
    <div ref="sentinel" class="h-4" />

    <!-- Sem mais resultados -->
    <div v-if="!hasMore && deals.length > 0" class="text-center py-8 text-sm text-gray-400">
      Você chegou ao fim das ofertas 🎉
    </div>
  </div>
</template>
